/**
 * Shared utilities for KYC forms (Priv + Org).
 * Extracted to eliminate duplication and Form.io artefacts.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { inputCls } from "../../../components";
import { COUNTRY_OPTIONS } from "../kycPriv/KycPrivForm.options";

// ── useScrollSpy ──────────────────────────────────────────────────────────────

/**
 * Tracks which section is in view and provides a smooth-scroll helper.
 * Walks up the DOM to find the actual scrollable container.
 */
export function useScrollSpy(ids: readonly string[]) {
  const [activeId, setActiveId] = useState<string>(ids[0]);
  const blocked = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const firstEl = document.getElementById(ids[0]);
    let scrollParent: Element | null = firstEl?.parentElement ?? null;
    while (scrollParent) {
      const { overflowY } = getComputedStyle(scrollParent);
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        scrollParent.scrollHeight > scrollParent.clientHeight
      )
        break;
      scrollParent = scrollParent.parentElement;
    }
    const target: EventTarget = scrollParent ?? window;
    const getContainerTop = () =>
      scrollParent ? scrollParent.getBoundingClientRect().top : 0;

    const onScroll = () => {
      if (blocked.current) return;
      const containerTop = getContainerTop();
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - containerTop <= 80) current = id;
      }
      setActiveId(current);
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => target.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = useCallback((id: string) => {
    blocked.current = true;
    setActiveId(id);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      blocked.current = false;
    }, 900);
  }, []);

  return { activeId, scrollTo };
}

// ── TocSidebar ────────────────────────────────────────────────────────────────

export function TocSidebar({
  sections,
  activeId,
  onNav,
}: {
  sections: readonly { id: string; label: string }[];
  activeId: string;
  onNav: (id: string) => void;
}) {
  return (
    <nav className="hidden md:flex flex-col gap-1 sticky top-4 w-44 shrink-0 pt-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1 px-2">
        Innhold
      </p>
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onNav(s.id)}
          className={`text-left px-2 py-1 rounded text-xs transition-colors ${
            activeId === s.id
              ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

// ── ErrorBanner ───────────────────────────────────────────────────────────────

export function ErrorBanner({ errors }: { errors: Record<string, unknown> }) {
  if (Object.keys(errors).length === 0) return null;
  return (
    <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
      <p className="font-semibold mb-1">
        Skjemaet inneholder feil som må rettes:
      </p>
      <ul className="list-disc pl-4 space-y-0.5">
        {Object.entries(errors).map(([key, err]) => {
          const msg =
            typeof err === "object" && err !== null && "message" in err
              ? (err as { message?: string }).message
              : "Påkrevd";
          if (!msg) return null;
          return <li key={key}>{msg}</li>;
        })}
      </ul>
    </div>
  );
}

// ── SurveyGrid ────────────────────────────────────────────────────────────────

/**
 * A table of radio buttons: rows are question items, columns are answer options.
 * Each row's `field` is registered directly — no indirection needed.
 */
export function SurveyGrid({
  rows,
  columns,
}: {
  rows: readonly { label: string; field: string }[];
  columns: { label: string; value: string }[];
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 pr-6 font-medium text-gray-700 min-w-[200px]" />
            {columns.map((col) => (
              <th
                key={col.value}
                className="text-center py-2 px-4 font-medium text-gray-700 whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hasError = !!errors[row.field];
            return (
              <tr
                key={row.field}
                className="border-b border-gray-100 last:border-0"
              >
                <td
                  className={`py-2 pr-6 ${
                    hasError ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  {row.label}
                </td>
                {columns.map((col) => (
                  <td key={col.value} className="text-center py-2 px-4">
                    <input
                      type="radio"
                      value={col.value}
                      {...register(row.field)}
                      className="accent-blue-600 w-3.5 h-3.5 cursor-pointer"
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── CountrySelect ─────────────────────────────────────────────────────────────

export function CountrySelect(
  props: React.SelectHTMLAttributes<HTMLSelectElement>
) {
  return (
    <select {...props} className={inputCls}>
      <option value="">– Velg –</option>
      {COUNTRY_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
